import inflection from 'inflection';
import _ from 'lodash';
import React from 'react';
import EntityRow from '../components/EntityRow';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import TextWithIcon from '../components/TextWithIcon';
import Timestamp from '../components/Timestamp';
import getEditorMentionUrl from '../editor/utils/getEditorMentionUrl';
import { MentionType } from '../graphql/generated/graphql';
import getExternalBrandLogoProps from '../integrations/getExternalBrandLogoProps';
import styles from './IntegrationLinkRow.module.scss';
import type { MetadataAttribute } from './PostRow';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const INTEGRATION_LINK_ROW_INTEGRATION_LINK_FRAGMENT = gql(`
  fragment IntegrationLinkRow_integrationLink on IntegrationLink {
    id
    title
    type
    externalId
    insertedBy {
      id
    }
    preview {
      original
    }
  }
`);

interface IntegrationLinkRowProps {
  inList: boolean;
  integrationLink: DocumentType<
    typeof INTEGRATION_LINK_ROW_INTEGRATION_LINK_FRAGMENT
  >;
  activePreviewId?: string | null;
  metadataAttribute?: MetadataAttribute | null;
  onActivePreviewIdChange?: ((id: string | null) => void) | null;
}

const getMetadata = (attribute: MetadataAttribute | null) => {
  const dummyViews = Math.round(Math.random() * 100);
  switch (attribute) {
    case 'popularity':
      return `${dummyViews} ${inflection.inflect('view', dummyViews)}`;
    case 'publishedAt':
    case 'userViewedAt':
    case 'insertedAt':
    default:
      return <Timestamp date={new Date().toISOString()} />;
  }
};

function getPath(
  integrationLink: DocumentType<
    typeof INTEGRATION_LINK_ROW_INTEGRATION_LINK_FRAGMENT
  >,
) {
  // TODO: remove / refactor this once https://github.com/slab/slab/issues/8421 is implemented
  switch (integrationLink.type) {
    case MentionType.AirtableTable:
      return `https://airtable.com/${integrationLink.externalId}`;
    case MentionType.FigmaFile: {
      const [key, nodeId] = integrationLink.externalId.split('/');
      return `https://figma.com/${key ?? ''}${
        nodeId ? `?node-id=${nodeId}` : ''
      }`;
    }
    case MentionType.MiroBoard:
      return `https://miro.com/app/board/${integrationLink.externalId}`;
    default: {
      return getEditorMentionUrl({
        id: integrationLink.externalId,
        type: _.kebabCase(integrationLink.type),
      });
    }
  }
}

const IntegrationLinkRow = ({
  inList,
  integrationLink,
  activePreviewId = null,
  metadataAttribute = null,
  onActivePreviewIdChange = null,
}: IntegrationLinkRowProps) => (
  <EntityRow
    title={<TextWithIcon text={integrationLink.title} />}
    inList={inList}
    meta={getMetadata(metadataAttribute)}
    preview={
      onActivePreviewIdChange && integrationLink.preview?.original
        ? {
            node: (
              <img
                src={integrationLink.preview.original}
                alt={`${integrationLink.title ?? 'preview'}`}
              />
            ),
            activePreviewId,
            onActivePreviewIdChange,
            id: integrationLink.id,
          }
        : undefined
    }
    contributorIds={[integrationLink.insertedBy.id]}
    icon={
      <div className={styles.integrationIconContainer}>
        <ExternalBrandLogo
          height={16}
          {...getExternalBrandLogoProps(_.kebabCase(integrationLink.type))}
        />
      </div>
    }
    path={getPath(integrationLink)}
  />
);

export default IntegrationLinkRow;
